import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { apiJson } from '../lib/api'
import { Failed, PendingButton, SkeletonRows } from '../components/pending'
import { PageHeader, PageBody, headerStyles } from '../components/PageHeader'
import { useChanged, useLayer } from '../components/layers'
import o from '../components/Overlay.module.css'
import styles from './IntegrationsPage.module.css'
import own from './ApiKeysPage.module.css'

// The workspace's Dataphyll Field devices (routes/field-devices.js, migration 181). A tablet is registered from its own
// screen: it shows a QR, someone who may add Sites & jobs scans it and signs in (DevicePairPage). Here it is renamed,
// or forgotten: it leaves the list and the tablet goes back to its registration screen the next time it reaches
// Control, after sending whatever it still holds (all of it is taken). Registering it again brings its row back.

export interface FieldDevice {
  id: string
  device_id: string
  name: string | null
  device_type: string | null
  form_factor: 'tablet' | 'phone' | null
  model: string | null
  platform: string | null
  app_version: string | null
  active: boolean
  registered_at: string | null
  registered_by_email: string | null
  last_seen_at: string | null
  deactivated_at: string | null
}

const APP_LABEL: Record<string, string> = {
  field_station: 'Weigh station',
  preassign: 'Preassign station',
  supervisor: 'Supervisor',
}

export const deviceAppLabel = (t: string | null) => (t && APP_LABEL[t]) || 'Weigh station'

// The client's weigh station settings (GET /api/devices/settings, migration 203). The cog is shown only while Client
// Setup ticks Weigh station; every station reads them on its next sync.
export interface StationSettings {
  bin_tag_required: boolean
  qr_scan: 'camera' | 'gun'
}

const BIN_TAG: { key: boolean; label: string; note: string }[] = [
  { key: true, label: 'Yes', note: 'A batch can only be completed once its bin’s tag ID is entered.' },
  { key: false, label: 'No', note: 'The bin ID may be left empty when a batch is completed.' },
]
const QR_SCAN: { key: StationSettings['qr_scan']; label: string; note: string }[] = [
  { key: 'gun', label: 'Gun', note: 'A scanner gun connected to the tablet reads the QR.' },
  { key: 'camera', label: 'Camera', note: 'The tablet’s camera reads the QR.' },
]

function StationSettingsModal({ user, settings, onClose, onSaved }: { user: ReturnType<typeof useAuth>['user']; settings: StationSettings; onClose: () => void; onSaved: (s: StationSettings) => void }) {
  const [binTag, setBinTag] = useState(settings.bin_tag_required)
  const [qrScan, setQrScan] = useState(settings.qr_scan)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const panelEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged({ binTag, qrScan })
  const { requestClose } = useLayer(true, onClose, { el: panelEl, modal: true, dirty, busy })

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      const r = await apiJson<{ settings: StationSettings }>(user, '/api/devices/settings', { method: 'PUT', body: { bin_tag_required: binTag, qr_scan: qrScan } })
      onSaved(r.settings)
    } catch (err) {
      setError(errText(err))
      setBusy(false)
    }
  }

  const choices = <K,>(label: string, list: { key: K; label: string; note: string }[], value: K, set: (k: K) => void) => (
    <div className={o.body}>
      <p className={o.label}>{label}</p>
      <div role="radiogroup" aria-label={label} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {list.map((c) => (
          <button key={String(c.key)} type="button" role="radio" aria-checked={value === c.key} className={value === c.key ? `${o.choice} ${o.choiceOn}` : o.choice} onClick={() => set(c.key)}>
            <span className={o.radio} />
            <span>
              <strong>{c.label}</strong>
              <div className={own.sub}>{c.note}</div>
            </span>
          </button>
        ))}
      </div>
    </div>
  )

  return (
    <div className={o.scrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
      <div ref={panelEl} tabIndex={-1} className={`${o.modal} ${o.narrow}`} role="dialog" aria-label="Weigh station settings">
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>Weigh station settings</h2>
            <p className={o.sub}>For every Dataphyll Field weigh station in this workspace. Each station picks up a change the next time it syncs.</p>
          </div>
        </div>
        {choices('Bin ID mandatory on batch close', BIN_TAG, binTag, setBinTag)}
        {choices('QR scan method', QR_SCAN, qrScan, setQrScan)}
        {error && <Failed text={error} />}
        <div className={o.foot}>
          <PendingButton type="button" className={o.primary} busy={busy} pending="Saving…" disabled={!dirty} onClick={save}>
            Save
          </PendingButton>
          <button type="button" className={o.ghost} onClick={requestClose} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

const PLATFORM_LABEL: Record<string, string> = { android: 'Android', ios: 'iPhone / iPad', web: 'Web' }

export const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'

const errText = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong.')

export function DevicesPage() {
  const { user } = useAuth()
  const [devices, setDevices] = useState<FieldDevice[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [failed, setFailed] = useState<{ at: string; text: string } | null>(null)
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null)
  // The device whose Forget has been pressed once: a second press forgets it.
  const [forgetting, setForgetting] = useState<string | null>(null)
  // null until read, and when the read fails: the cog is simply not offered.
  const [station, setStation] = useState<{ weigh_station: boolean; settings: StationSettings } | null>(null)
  const [cogOpen, setCogOpen] = useState(false)
  const [saved, setSaved] = useState(false)

  const load = useCallback(async () => {
    setLoadError(null)
    try {
      setDevices((await apiJson<{ devices: FieldDevice[] }>(user, '/api/devices')).devices)
    } catch (err) {
      setLoadError(errText(err))
    }
  }, [user])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    apiJson<{ weigh_station: boolean; settings: StationSettings }>(user, '/api/devices/settings')
      .then(setStation)
      .catch(() => setStation(null))
  }, [user])

  const change = async (d: FieldDevice, body: { name?: string; active?: boolean }) => {
    setBusy(d.id)
    setFailed(null)
    try {
      const r = await apiJson<{ device: FieldDevice }>(user, `/api/devices/${d.id}`, { method: 'PATCH', body })
      setDevices((list) => (list || []).flatMap((x) => (x.id !== d.id ? [x] : r.device.active ? [r.device] : [])))
      setRenaming(null)
      setForgetting(null)
    } catch (err) {
      setFailed({ at: d.id, text: errText(err) })
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <PageHeader
        title="Devices"
        actions={
          station?.weigh_station ? (
            <button type="button" className={headerStyles.iconBtn} title="Weigh station settings: bin ID on batch close and QR scan method" aria-label="Weigh station settings" onClick={() => setCogOpen(true)}>
              ⚙
            </button>
          ) : undefined
        }
      />
      {cogOpen && station && (
        <StationSettingsModal
          user={user}
          settings={station.settings}
          onClose={() => setCogOpen(false)}
          onSaved={(settings) => {
            setStation({ ...station, settings })
            setCogOpen(false)
            setSaved(true)
          }}
        />
      )}
      <PageBody>
        <div className={own.notice} data-testid="devices-notice">
          <p>
            <strong>Dataphyll Field tablets and phones registered to this workspace.</strong> To add one, open Dataphyll Field on it and scan the QR
            it shows. Then sign in, choose this workspace and name the device.
          </p>
          <p>
            Forgetting a device takes it off this list. The next time it reaches Control it sends anything it still holds, then goes back to its
            registration screen. Its records stay, and registering it again brings it back.
          </p>
        </div>
        {saved && (
          <p role="status" className={own.sub}>
            Weigh station settings saved. Each station picks them up the next time it syncs.
          </p>
        )}

        {loadError ? (
          <div className={styles.error} role="alert">
            <p>{loadError}</p>
            <button onClick={load}>Retry</button>
          </div>
        ) : (
          <section className={own.list} aria-label="Devices">
            <h2 className={own.listHead}>Registered devices</h2>
            {devices === null ? (
              <SkeletonRows rowClass={own.row} cols={5} rows={3} label="Loading devices" />
            ) : devices.length === 0 ? (
              <div className={styles.empty}>
                <p>No devices yet. Open Dataphyll Field on a tablet and scan its QR to register it here.</p>
              </div>
            ) : (
              <div className={own.tableCard}>
                <table className={own.table} data-testid="devices">
                  <thead>
                    <tr>
                      <th>Device</th>
                      <th>Used as</th>
                      <th>Version</th>
                      <th>Registered</th>
                      <th>Last seen</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {devices.map((d) => (
                      <tr key={d.id} data-testid="device-row">
                        <td>
                          {renaming?.id === d.id ? (
                            <form
                              className={styles.keyRow}
                              onSubmit={(e) => {
                                e.preventDefault()
                                if (renaming.name.trim()) change(d, { name: renaming.name.trim() })
                              }}
                            >
                              <input
                                className={styles.input}
                                value={renaming.name}
                                maxLength={80}
                                autoFocus
                                aria-label="Device name"
                                onChange={(e) => setRenaming({ id: d.id, name: e.target.value })}
                              />
                              <PendingButton type="submit" className={styles.primary} busy={busy === d.id} pending="Saving…" disabled={!renaming.name.trim() || !!busy}>
                                Save
                              </PendingButton>
                              <button type="button" className={styles.button} disabled={busy === d.id} onClick={() => setRenaming(null)}>
                                Cancel
                              </button>
                            </form>
                          ) : (
                            <>
                              {d.name || d.device_id}
                              <div className={own.sub}>
                                <code>{d.device_id}</code>
                                {d.model ? ` · ${d.model}` : ''}
                              </div>
                            </>
                          )}
                        </td>
                        <td>
                          {deviceAppLabel(d.device_type)}
                          {d.form_factor && <div className={own.sub}>{d.form_factor === 'phone' ? 'Phone' : 'Tablet'}</div>}
                        </td>
                        <td>
                          {d.app_version || '—'}
                          {d.platform && PLATFORM_LABEL[d.platform] && <div className={own.sub}>{PLATFORM_LABEL[d.platform]}</div>}
                        </td>
                        <td>
                          {when(d.registered_at)}
                          {d.registered_by_email && <div className={own.sub}>by {d.registered_by_email}</div>}
                        </td>
                        <td>{when(d.last_seen_at)}</td>
                        <td className={own.actions}>
                          {renaming?.id !== d.id && (
                            <>
                              <button type="button" className={styles.button} disabled={!!busy} onClick={() => setRenaming({ id: d.id, name: d.name || d.device_id })}>
                                Rename
                              </button>{' '}
                              <PendingButton
                                className={own.revokeBtn}
                                busy={busy === d.id}
                                pending="Forgetting…"
                                disabled={!!busy}
                                onClick={() => (forgetting === d.id ? change(d, { active: false }) : setForgetting(d.id))}
                                onBlur={() => forgetting === d.id && !busy && setForgetting(null)}
                              >
                                {forgetting === d.id ? 'Really forget?' : 'Forget'}
                              </PendingButton>
                            </>
                          )}
                          {failed?.at === d.id && <Failed text={failed.text} />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </PageBody>
    </>
  )
}
